import { useEffect, useCallback, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AdStudioPersistence, AdStudioState, AdStudioFormData } from '../utils/adStudioPersistence';
import { MediaFile } from '../services/mediaService';
import { useAITask } from '../contexts/AITaskContext';
import { form } from 'framer-motion/client';
import { form } from 'framer-motion/client';
import { form } from 'framer-motion/client';
import { form } from 'framer-motion/client';

interface UseAdStudioPersistenceProps {
  form: UseFormReturn<AdStudioFormData>;
  selectedImage: MediaFile[];
  selectedProducts: MediaFile[];
  selectedReferenceImages: MediaFile[];
  selectedCredibilityImages: MediaFile[];
  multiProductMode: boolean;
  textOnlyMode: boolean;
  useBrandify: boolean;
  showAdvanced: boolean;
  showInfluenceTuning: boolean;
  textPrompt: string;
  generatedImages: string[];
  setSelectedImage: (images: MediaFile[]) => void;
  setSelectedProducts: (products: MediaFile[]) => void;
  setSelectedReferenceImages: (images: MediaFile[]) => void;
  setSelectedCredibilityImages: (images: MediaFile[]) => void;
  setMultiProductMode: (mode: boolean) => void;
  setTextOnlyMode: (mode: boolean) => void;
  setUseBrandify: (use: boolean) => void;
  setShowAdvanced: (show: boolean) => void;
  setShowInfluenceTuning: (show: boolean) => void;
  setTextPrompt: (prompt: string) => void;
  setGeneratedImages: (images: string[]) => void;
  isEnhancing: boolean;
  hasActiveSession: boolean;
  setHasActiveSession: (active: boolean) => void;
  generationProgress: number;
  generationStartTime: number | null;
  setGenerationProgress: (progress: number) => void;
  setGenerationStartTime: (time: number | null) => void;
  // User context for security
  currentUserId?: string;
  currentClientId?: string;
}

export const useAdStudioPersistence = ({
  form,
  selectedImage,
  selectedProducts,
  selectedReferenceImages,
  selectedCredibilityImages,
  multiProductMode,
  textOnlyMode,
  useBrandify,
  showAdvanced,
  showInfluenceTuning,
  textPrompt,
  generatedImages,
  setSelectedImage,
  setSelectedProducts,
  setSelectedReferenceImages,
  setSelectedCredibilityImages,
  setMultiProductMode,
  setTextOnlyMode,
  setUseBrandify,
  setShowAdvanced,
  setShowInfluenceTuning,
  setTextPrompt,
  setGeneratedImages,
  isEnhancing,
  hasActiveSession,
  setHasActiveSession,
  generationProgress,
  generationStartTime,
  setGenerationProgress,
  setGenerationStartTime,
  currentUserId,
  currentClientId,
}: UseAdStudioPersistenceProps) => {
  const { getRecentCompletedTask, isTaskRunning } = useAITask();
  const hasInitialized = useRef(false);
  const isRestoring = useRef(false);
  const sessionId = useRef<string | null>(null);
  const userChangedFields = useRef<Set<string>>(new Set()); // Track which fields user has changed
  const userHasMadeChanges = useRef(false); // Flag to stop all persistence interference

  // Set user context when available and clear any existing state from other users
  useEffect(() => {
    if (currentUserId && currentClientId) {
      console.log('👤 Setting user context and clearing any foreign state');
      AdStudioPersistence.setUserContext(currentUserId, currentClientId);
      
      // Force clear any state that might belong to other users
      const currentState = AdStudioPersistence.loadState();
      if (currentState.userId && currentState.userId !== currentUserId) {
        console.log('🗑️ Clearing foreign user state immediately');
        AdStudioPersistence.clearState();
        
        // Reset all local state to defaults
        setSelectedImage([]);
        setSelectedProducts([]);
        setSelectedReferenceImages([]);
        setSelectedCredibilityImages([]);
        setMultiProductMode(false);
        setTextOnlyMode(false);
        setUseBrandify(false);
        setShowAdvanced(false);
        setShowInfluenceTuning(false);
        setTextPrompt('');
        setGeneratedImages([]);
        
        // Reset form to defaults
        form.reset();
        
        // Mark as active session to prevent restoration
        setHasActiveSession(true);
      }
    }
  }, [currentUserId, currentClientId]);

  // Clear user context when user logs out (user/client becomes null)
  useEffect(() => {
    if (!currentUserId || !currentClientId) {
      console.log('👤 User logged out, clearing Ad Studio context');
      AdStudioPersistence.clearUserContext();
      
      // Also clear all local state
      setSelectedImage([]);
      setSelectedProducts([]);
      setSelectedReferenceImages([]);
      setSelectedCredibilityImages([]);
      setMultiProductMode(false);
      setTextOnlyMode(false);
      setUseBrandify(false);
      setShowAdvanced(false);
      setShowInfluenceTuning(false);
      setTextPrompt('');
      setGeneratedImages([]);
      form.reset();
    }
  }, [currentUserId, currentClientId]);

  // Initialize session and check for state restoration
  useEffect(() => {
    if (hasInitialized.current) return;
    
    // Don't initialize without user context
    if (!currentUserId || !currentClientId) {
      console.log('⏳ Waiting for user context before initializing Ad Studio session...');
      return;
    }
    
    console.log('🔄 Initializing Ad Studio session for user:', currentUserId);
    
    // Initialize new session
    sessionId.current = AdStudioPersistence.initializeSession();
    
    // Check if we should restore state (and we haven't set an active session yet)
    if (AdStudioPersistence.shouldRestoreState()) {
      console.log('📂 Checking for state to restore...');
      const savedState = AdStudioPersistence.loadState();
      console.log('📂 Loaded state:', {
        hasFormData: !!savedState.formData,
        selectedImageCount: savedState.selectedImage?.length || 0,
        selectedProductsCount: savedState.selectedProducts?.length || 0,
        generatedImagesCount: savedState.generatedImages?.length || 0,
        textPromptLength: savedState.textPrompt?.length || 0,
        userId: savedState.userId,
        clientId: savedState.clientId
      });
      
      // SECURITY CHECK: Validate user ownership if user info is present in state
      if (savedState.userId && savedState.userId !== currentUserId) {
        console.log('🚫 State belongs to different user, clearing');
        console.log('State user:', savedState.userId, 'Current user:', currentUserId);
        AdStudioPersistence.clearState();
        setHasActiveSession(true);
        hasInitialized.current = true;
        return;
      }
      
      if (savedState.clientId && savedState.clientId !== currentClientId) {
        console.log('🚫 State belongs to different client, clearing');
        console.log('State client:', savedState.clientId, 'Current client:', currentClientId);
        AdStudioPersistence.clearState();
        setHasActiveSession(true);
        hasInitialized.current = true;
        return;
      }
      
      // If no user info in state, it's likely from before the security fix - allow it but log
      if (!savedState.userId || !savedState.clientId) {
        console.log('⚠️ State missing user info (legacy state), allowing restoration but will add user info on save');
      }
      
      const hasStateToRestore = savedState && (
        savedState.selectedImage.length > 0 || 
        savedState.selectedProducts.length > 0 || 
        savedState.textPrompt.length > 0 || 
        savedState.generatedImages.length > 0
      );
      
      if (hasStateToRestore) {
        console.log('📂 Restoring Ad Studio state from previous session:', savedState);
        isRestoring.current = true;
        
        // Restore form data (only if user hasn't made any changes yet)
        if (savedState.formData && !userHasMadeChanges.current) {
          Object.entries(savedState.formData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              form.setValue(key as keyof AdStudioFormData, value);
            }
          });
        }
        
        // Restore mode states
        setMultiProductMode(savedState.multiProductMode);
        setTextOnlyMode(savedState.textOnlyMode);
        setUseBrandify(savedState.useBrandify);
        setShowAdvanced(savedState.showAdvanced);
        setShowInfluenceTuning(savedState.showInfluenceTuning);
        
        // Restore selected media
        console.log('📷 Restoring selected media:', {
          selectedImage: savedState.selectedImage?.length || 0,
          selectedProducts: savedState.selectedProducts?.length || 0,
          selectedReferenceImages: savedState.selectedReferenceImages?.length || 0,
          selectedCredibilityImages: savedState.selectedCredibilityImages?.length || 0
        });
        setSelectedImage(savedState.selectedImage || []);
        setSelectedProducts(savedState.selectedProducts || []);
        setSelectedReferenceImages(savedState.selectedReferenceImages || []);
        setSelectedCredibilityImages(savedState.selectedCredibilityImages || []);
        
        // Restore text prompt
        setTextPrompt(savedState.textPrompt || '');
        
        // Restore generated images (will be checked for running tasks in separate effect)
        if (savedState.generatedImages && savedState.generatedImages.length > 0) {
          console.log('🖼️ Restoring generated images:', savedState.generatedImages.length, 'images');
          console.log('🖼️ Generated image URLs:', savedState.generatedImages);
          setGeneratedImages(savedState.generatedImages);
        } else {
          console.log('🖼️ No generated images to restore');
        }
        
        // Restore generation timing state if generation was in progress
        if (savedState.generationStartTime && isEnhancing) {
          console.log('⏱️ Restoring generation timing state:', {
            startTime: savedState.generationStartTime,
            progress: savedState.generationProgress
          });
          setGenerationStartTime(savedState.generationStartTime);
          setGenerationProgress(savedState.generationProgress || 0);
        }
        
        setTimeout(() => {
          isRestoring.current = false;
        }, 100);
        
        // Don't set hasActiveSession to true after restoration - we want to show indicators
      } else {
        console.log('📝 No meaningful state to restore');
        setHasActiveSession(true); // Mark as active session
      }
    } else {
      console.log('📝 No stored state - starting fresh');
      setHasActiveSession(true); // Mark as active session for same-page usage
    }
    
    hasInitialized.current = true;
  }, [hasActiveSession, currentUserId, currentClientId]);

  // Watch for user changes to form fields and mark them as user-controlled
  const watchedFormValues = form.watch();
  const previousFormValues = useRef<any>(null);
  
  useEffect(() => {
    if (!hasInitialized.current || isRestoring.current) return;
    
    // Compare current values with previous to detect user changes
    if (previousFormValues.current) {
      Object.keys(watchedFormValues).forEach(fieldName => {
        if (watchedFormValues[fieldName] !== previousFormValues.current[fieldName]) {
          console.log(`🔄 User changed field: ${fieldName} from ${previousFormValues.current[fieldName]} to ${watchedFormValues[fieldName]}`);
          userChangedFields.current.add(fieldName);
          userHasMadeChanges.current = true; // User has made changes, stop all persistence interference
          
          // Mark as active session since user is actively working
          if (!hasActiveSession) {
            setHasActiveSession(true);
          }
        }
      });
    }
    
    previousFormValues.current = { ...watchedFormValues };
  }, [watchedFormValues, form]);



  // Check for completed tasks and restore results (only for cross-navigation scenarios)
  // SECURITY NOTE: AI Task context restoration disabled, using only localStorage-based persistence
  useEffect(() => {
    if (!hasInitialized.current || isEnhancing || hasActiveSession) return;
    
    // Only use localStorage-based persistence, not AI Task context
    // The AI Task context doesn't filter by user, so we rely on our user-specific localStorage
    console.log('📂 Using localStorage-based persistence only (AI Task context disabled for security)');
    
  }, [isEnhancing, hasActiveSession]);

  // Save current state when user navigates away
  const saveStateForNavigation = useCallback(() => {
    if (isRestoring.current) return;
    
    console.log('🚪 Saving state for navigation...', {
      selectedImageCount: selectedImage.length,
      selectedProductsCount: selectedProducts.length,
      selectedCredibilityImagesCount: selectedCredibilityImages.length,
      generatedImagesCount: generatedImages.length,
      generatedImages: generatedImages
    });
    const currentState = {
      formData: form.getValues(),
      multiProductMode,
      textOnlyMode,
      useBrandify,
      showAdvanced,
      showInfluenceTuning,
      selectedImage,
      selectedProducts,
      selectedReferenceImages,
      selectedCredibilityImages,
      textPrompt,
      generatedImages,
      generationProgress,
      generationStartTime,
      // Always include user info for security
      userId: currentUserId,
      clientId: currentClientId,
    };
    
    console.log('🚪 Full state being saved:', currentState);
    AdStudioPersistence.saveStateOnNavigation(currentState);
  }, [form, multiProductMode, textOnlyMode, useBrandify, showAdvanced, showInfluenceTuning, selectedImage, selectedProducts, selectedReferenceImages, selectedCredibilityImages, textPrompt, generatedImages, currentUserId, currentClientId]);

  // Setup navigation listener
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveStateForNavigation();
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveStateForNavigation();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveStateForNavigation]);

  // No automatic persistence during same session - only save on navigation

  // Clear state when user explicitly starts a new generation
  const clearPersistedState = useCallback(() => {
    console.log('🗑️ Clearing persisted Ad Studio state');
    AdStudioPersistence.clearState();
  }, []);

  // Get state summary for debugging
  const getStateSummary = useCallback(() => {
    return AdStudioPersistence.getStateSummary();
  }, []);

  return {
    clearPersistedState,
    getStateSummary,
    hasPersistedState: () => {
      return !hasActiveSession && AdStudioPersistence.hasPersistedStateToRestore();
    },
    saveStateForNavigation, // Expose for manual saving when needed
  };
};